import type { SearchableSelectOption } from '@kss/ui';

/**
 * Storefront-facing dropdown sources for the address picker.
 *
 * Pincode lookups (`pincodeLookup.ts`) are the primary way users fill
 * out city + state — it auto-completes from a 6-digit ZIP. These
 * lists are the *fallback* used when the pincode service can't be
 * reached or when the user is editing a country other than India.
 *
 * Storefront values are plain readable names ("Mumbai", "Maharashtra",
 * "India") — not GST codes — because the values are persisted into
 * `shippingAddressJson` as free-text and rendered back to the
 * customer on the order detail page.
 */

export const INDIAN_STATES: SearchableSelectOption[] = [
  { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
  { value: 'Arunachal Pradesh', label: 'Arunachal Pradesh' },
  { value: 'Assam', label: 'Assam' },
  { value: 'Bihar', label: 'Bihar' },
  { value: 'Chhattisgarh', label: 'Chhattisgarh' },
  { value: 'Goa', label: 'Goa' },
  { value: 'Gujarat', label: 'Gujarat' },
  { value: 'Haryana', label: 'Haryana' },
  { value: 'Himachal Pradesh', label: 'Himachal Pradesh' },
  { value: 'Jharkhand', label: 'Jharkhand' },
  { value: 'Karnataka', label: 'Karnataka' },
  { value: 'Kerala', label: 'Kerala' },
  { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
  { value: 'Maharashtra', label: 'Maharashtra' },
  { value: 'Manipur', label: 'Manipur' },
  { value: 'Meghalaya', label: 'Meghalaya' },
  { value: 'Mizoram', label: 'Mizoram' },
  { value: 'Nagaland', label: 'Nagaland' },
  { value: 'Odisha', label: 'Odisha' },
  { value: 'Punjab', label: 'Punjab' },
  { value: 'Rajasthan', label: 'Rajasthan' },
  { value: 'Sikkim', label: 'Sikkim' },
  { value: 'Tamil Nadu', label: 'Tamil Nadu' },
  { value: 'Telangana', label: 'Telangana' },
  { value: 'Tripura', label: 'Tripura' },
  { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
  { value: 'Uttarakhand', label: 'Uttarakhand' },
  { value: 'West Bengal', label: 'West Bengal' },
  // Union Territories
  { value: 'Andaman and Nicobar Islands', label: 'Andaman & Nicobar Islands', description: 'UT' },
  { value: 'Chandigarh', label: 'Chandigarh', description: 'UT' },
  { value: 'Dadra and Nagar Haveli and Daman and Diu', label: 'Dadra & Nagar Haveli and Daman & Diu', description: 'UT' },
  { value: 'Delhi', label: 'Delhi', description: 'UT' },
  { value: 'Jammu and Kashmir', label: 'Jammu & Kashmir', description: 'UT' },
  { value: 'Ladakh', label: 'Ladakh', description: 'UT' },
  { value: 'Lakshadweep', label: 'Lakshadweep', description: 'UT' },
  { value: 'Puducherry', label: 'Puducherry', description: 'UT' },
];

export const INDIAN_CITIES: SearchableSelectOption[] = [
  { value: 'Mumbai', label: 'Mumbai' },
  { value: 'Delhi', label: 'Delhi' },
  { value: 'Bengaluru', label: 'Bengaluru' },
  { value: 'Hyderabad', label: 'Hyderabad' },
  { value: 'Chennai', label: 'Chennai' },
  { value: 'Kolkata', label: 'Kolkata' },
  { value: 'Pune', label: 'Pune' },
  { value: 'Ahmedabad', label: 'Ahmedabad' },
  { value: 'Jaipur', label: 'Jaipur' },
  { value: 'Surat', label: 'Surat' },
  { value: 'Lucknow', label: 'Lucknow' },
  { value: 'Kanpur', label: 'Kanpur' },
  { value: 'Nagpur', label: 'Nagpur' },
  { value: 'Indore', label: 'Indore' },
  { value: 'Thane', label: 'Thane' },
  { value: 'Bhopal', label: 'Bhopal' },
  { value: 'Visakhapatnam', label: 'Visakhapatnam' },
  { value: 'Patna', label: 'Patna' },
  { value: 'Vadodara', label: 'Vadodara' },
  { value: 'Ghaziabad', label: 'Ghaziabad' },
  { value: 'Ludhiana', label: 'Ludhiana' },
  { value: 'Agra', label: 'Agra' },
  { value: 'Nashik', label: 'Nashik' },
  { value: 'Faridabad', label: 'Faridabad' },
  { value: 'Meerut', label: 'Meerut' },
  { value: 'Rajkot', label: 'Rajkot' },
  { value: 'Varanasi', label: 'Varanasi' },
  { value: 'Srinagar', label: 'Srinagar' },
  { value: 'Aurangabad', label: 'Aurangabad' },
  { value: 'Amritsar', label: 'Amritsar' },
  { value: 'Allahabad', label: 'Allahabad' },
  { value: 'Howrah', label: 'Howrah' },
  { value: 'Coimbatore', label: 'Coimbatore' },
  { value: 'Jabalpur', label: 'Jabalpur' },
  { value: 'Gwalior', label: 'Gwalior' },
  { value: 'Vijayawada', label: 'Vijayawada' },
  { value: 'Madurai', label: 'Madurai' },
  { value: 'Raipur', label: 'Raipur' },
  { value: 'Kota', label: 'Kota' },
  { value: 'Chandigarh', label: 'Chandigarh' },
  { value: 'Guwahati', label: 'Guwahati' },
  { value: 'Mysore', label: 'Mysore' },
  { value: 'Tonk', label: 'Tonk' },
];

export const COUNTRIES: SearchableSelectOption[] = [
  { value: 'India', label: 'India', description: '🇮🇳 IN' },
  { value: 'United States', label: 'United States', description: '🇺🇸 US' },
  { value: 'United Kingdom', label: 'United Kingdom', description: '🇬🇧 GB' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates', description: '🇦🇪 AE' },
  { value: 'Australia', label: 'Australia', description: '🇦🇺 AU' },
  { value: 'Canada', label: 'Canada', description: '🇨🇦 CA' },
  { value: 'Singapore', label: 'Singapore', description: '🇸🇬 SG' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia', description: '🇸🇦 SA' },
  { value: 'Germany', label: 'Germany', description: '🇩🇪 DE' },
  { value: 'France', label: 'France', description: '🇫🇷 FR' },
  { value: 'Japan', label: 'Japan', description: '🇯🇵 JP' },
  { value: 'China', label: 'China', description: '🇨🇳 CN' },
  { value: 'Bangladesh', label: 'Bangladesh', description: '🇧🇩 BD' },
  { value: 'Sri Lanka', label: 'Sri Lanka', description: '🇱🇰 LK' },
  { value: 'Nepal', label: 'Nepal', description: '🇳🇵 NP' },
  { value: 'Bhutan', label: 'Bhutan', description: '🇧🇹 BT' },
  { value: 'Maldives', label: 'Maldives', description: '🇲🇻 MV' },
  { value: 'Pakistan', label: 'Pakistan', description: '🇵🇰 PK' },
  { value: 'Malaysia', label: 'Malaysia', description: '🇲🇾 MY' },
  { value: 'Thailand', label: 'Thailand', description: '🇹🇭 TH' },
];
